import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import config from '../config/config';

const TOOLS = [
  {
    id: 'syft',
    label: 'Syft',
    badgeClass: 'bg-success',
    commands: [
      { label: 'Lokale directory', code: 'syft /pad/naar/project -o spdx-json > mijn-project-sbom.json' },
      { label: 'Docker image', code: 'syft nginx:latest -o spdx-json > nginx-sbom.json' },
      { label: 'Git repository', code: 'syft git+https://github.com/org/repo.git -o spdx-json > repo-sbom.json' },
    ],
    install: 'curl -sSfL https://raw.githubusercontent.com/anchore/syft/main/install.sh | sh -s -- -b /usr/local/bin',
  },
  {
    id: 'cdxgen',
    label: 'cdxgen',
    badgeClass: 'bg-info text-dark',
    commands: [
      { label: 'Lokale directory', code: 'cdxgen /pad/naar/project -o cyclonedx -p > mijn-project-sbom.json' },
      { label: 'Docker image', code: 'cdxgen --image nginx:latest -o cyclonedx > nginx-sbom.json' },
    ],
    install: 'npm install -g @cyclonedx/cdxgen',
  },
  {
    id: 'trivy',
    label: 'Trivy',
    badgeClass: 'bg-warning text-dark',
    commands: [
      { label: 'Lokale directory', code: 'trivy fs /pad/naar/project --format spdx-json --output sbom.json' },
      { label: 'Docker image', code: 'trivy image nginx:latest --format spdx-json --output nginx-sbom.json' },
    ],
    install: 'brew install aquasecurity/trivy/trivy',
  },
];

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={copy} className="btn btn-sm btn-outline-secondary ms-2" style={{ fontSize: '0.7rem', padding: '1px 8px' }}>
      {copied ? `✓ ${t('upload.copied')}` : t('upload.copyBtn')}
    </button>
  );
}

function SbomUpload() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTool, setActiveTool] = useState('syft');
  const [sourceType, setSourceType] = useState('upload');
  const [repoName, setRepoName] = useState('');
  const [imageRef, setImageRef] = useState('');
  const [chartPath, setChartPath] = useState('');
  const [terraformPath, setTerraformPath] = useState('');
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef();

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  }, []);

  const handleUpload = async () => {
    if (!repoName.trim()) { setError(t('upload.errors.repoRequired')); return; }
    if (sourceType === 'upload' && !file) { setError(t('upload.errors.fileRequired')); return; }
    if (sourceType === 'docker-image' && !imageRef.trim()) { setError(t('upload.errors.imageRefRequired')); return; }
    if (sourceType === 'helm-chart' && !chartPath.trim()) { setError(t('upload.errors.chartPathRequired')); return; }
    if (sourceType === 'terraform' && !terraformPath.trim()) { setError(t('upload.errors.terraformPathRequired')); return; }
    setError('');
    setResult(null);
    setUploading(true);

    try {
      let data;
      if (sourceType === 'docker-image') {
        ({ data } = await axios.post(
          `${config.BASE_URL}/sbom/${encodeURIComponent(repoName.trim())}/generate`,
          { sourceType: 'docker-image', imageRef: imageRef.trim() }
        ));
      } else if (sourceType === 'helm-chart') {
        ({ data } = await axios.post(
          `${config.BASE_URL}/sbom/${encodeURIComponent(repoName.trim())}/generate`,
          { sourceType: 'helm-chart', chartPath: chartPath.trim() }
        ));
      } else if (sourceType === 'terraform') {
        ({ data } = await axios.post(
          `${config.BASE_URL}/sbom/${encodeURIComponent(repoName.trim())}/generate`,
          { sourceType: 'terraform', terraformPath: terraformPath.trim() }
        ));
      } else {
        const formData = new FormData();
        formData.append('file', file);
        ({ data } = await axios.post(
          `${config.BASE_URL}/sbom/${encodeURIComponent(repoName.trim())}/upload`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        ));
      }
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setUploading(false);
    }
  };

  const activeTool_ = TOOLS.find(t => t.id === activeTool);

  return (
    <div className="App-body" style={{ maxWidth: 960 }}>

      {/* Page header */}
      <div className="ce-dashboard-header" style={{ marginBottom: '1.75rem' }}>
        <div>
          <h1 className="ce-page-title">📦 {t('upload.title')}</h1>
          <p className="ce-page-subtitle">{t('upload.subtitle')}</p>
        </div>
      </div>

      <div className="row g-4">

        {/* LEFT: Upload form */}
        <div className="col-lg-5">
          <div className="ce-card h-100">
            <div className="ce-card-header">
              {sourceType === 'docker-image'
                ? `🐳 ${t('upload.generateDockerCard')}`
                : sourceType === 'helm-chart'
                  ? `☸️ ${t('upload.generateHelmCard')}`
                  : sourceType === 'terraform'
                    ? `🏗️ ${t('upload.generateTerraformCard')}`
                  : `⬆️ ${t('upload.uploadCard')}`}
            </div>
            <div className="ce-card-body d-flex flex-column gap-3">

              <div>
                <label className="form-label fw-semibold small">{t('upload.sourceType')}</label>
                <div className="d-flex gap-2 flex-wrap">
                  <button
                    type="button"
                    className={`ce-btn ce-btn--sm ${sourceType === 'upload' ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
                    onClick={() => {
                      setSourceType('upload');
                      setResult(null);
                      setError('');
                    }}
                  >
                    {t('upload.sourceUpload')}
                  </button>
                  <button
                    type="button"
                    className={`ce-btn ce-btn--sm ${sourceType === 'docker-image' ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
                    onClick={() => {
                      setSourceType('docker-image');
                      setResult(null);
                      setError('');
                    }}
                  >
                    {t('upload.sourceDocker')}
                  </button>
                  <button
                    type="button"
                    className={`ce-btn ce-btn--sm ${sourceType === 'helm-chart' ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
                    onClick={() => {
                      setSourceType('helm-chart');
                      setResult(null);
                      setError('');
                    }}
                  >
                    {t('upload.sourceHelm')}
                  </button>
                  <button
                    type="button"
                    className={`ce-btn ce-btn--sm ${sourceType === 'terraform' ? 'ce-btn--primary' : 'ce-btn--secondary'}`}
                    onClick={() => {
                      setSourceType('terraform');
                      setResult(null);
                      setError('');
                    }}
                  >
                    {t('upload.sourceTerraform')}
                  </button>
                </div>
                <div className="form-text">{t('upload.sourceTypeHint')}</div>
              </div>

              {/* Repo name */}
              <div>
                <label className="form-label fw-semibold small">{t('upload.repoName')} <span className="text-danger">*</span></label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('upload.repoNamePlaceholder')}
                  value={repoName}
                  onChange={e => setRepoName(e.target.value)}
                />
                <div className="form-text">{t('upload.repoNameHint')}</div>
              </div>

              {sourceType === 'docker-image' ? (
                <div>
                  <label className="form-label fw-semibold small">{t('upload.imageRefLabel')} <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t('upload.imageRefPlaceholder')}
                    value={imageRef}
                    onChange={e => setImageRef(e.target.value)}
                  />
                  <div className="form-text">{t('upload.imageRefHint')}</div>
                </div>
              ) : sourceType === 'helm-chart' ? (
                <div>
                  <label className="form-label fw-semibold small">{t('upload.chartPathLabel')} <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t('upload.chartPathPlaceholder')}
                    value={chartPath}
                    onChange={e => setChartPath(e.target.value)}
                  />
                  <div className="form-text">{t('upload.chartPathHint')}</div>
                </div>
              ) : sourceType === 'terraform' ? (
                <div>
                  <label className="form-label fw-semibold small">{t('upload.terraformPathLabel')} <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t('upload.terraformPathPlaceholder')}
                    value={terraformPath}
                    onChange={e => setTerraformPath(e.target.value)}
                  />
                  <div className="form-text">{t('upload.terraformPathHint')}</div>
                </div>
              ) : (
                <div>
                  <label className="form-label fw-semibold small">{t('upload.fileLabel')} <span className="text-danger">*</span></label>
                  <div
                    onDragOver={e => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current.click()}
                    className={`border rounded-3 d-flex flex-column align-items-center justify-content-center p-4 text-center ${dragging ? 'border-primary bg-primary bg-opacity-10' : 'border-dashed'}`}
                    style={{ cursor: 'pointer', minHeight: 120, borderStyle: 'dashed', transition: 'all 0.2s' }}
                  >
                    <input ref={fileInputRef} type="file" accept=".json" style={{ display: 'none' }} onChange={e => setFile(e.target.files[0])} />
                    {file ? (
                      <>
                        <span style={{ fontSize: '1.5rem' }}>✅</span>
                        <div className="fw-semibold mt-1 small">{file.name}</div>
                        <div className="text-secondary" style={{ fontSize: '0.75rem' }}>{(file.size / 1024).toFixed(1)} KB — klik om te wijzigen</div>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: '1.5rem' }}>📂</span>
                        <div className="mt-1 small text-secondary">{t('upload.dropZoneHint')}</div>
                        <div className="mt-1 text-secondary" style={{ fontSize: '0.7rem' }}>{t('upload.dropZoneFormats')}</div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Supported formats */}
              {sourceType === 'upload' ? (
                <div className="d-flex gap-2">
                  <span className="badge bg-secondary">SPDX-JSON</span>
                  <span className="badge bg-secondary">CycloneDX-JSON</span>
                  <span className="badge bg-light text-dark border">{t('upload.maxSize')}</span>
                </div>
              ) : sourceType === 'terraform' ? (
                <div className="d-flex gap-2 flex-wrap">
                  <span className="badge bg-secondary">Terraform</span>
                  <span className="badge bg-secondary">Local directory</span>
                  <span className="badge bg-light text-dark border">{t('upload.terraformSupportHint')}</span>
                </div>
              ) : sourceType === 'helm-chart' ? (
                <div className="d-flex gap-2 flex-wrap">
                  <span className="badge bg-secondary">Helm chart</span>
                  <span className="badge bg-secondary">Local directory</span>
                  <span className="badge bg-light text-dark border">{t('upload.helmSupportHint')}</span>
                </div>
              ) : (
                <div className="d-flex gap-2 flex-wrap">
                  <span className="badge bg-secondary">Docker image</span>
                  <span className="badge bg-secondary">Syft</span>
                  <span className="badge bg-light text-dark border">{t('upload.dockerSupportHint')}</span>
                </div>
              )}

              {/* Error */}
              {error && <div className="alert alert-danger py-2 small mb-0">{error}</div>}

              {/* Success */}
              {result && (
                <div className="alert alert-success py-2 small mb-0">
                  <div className="fw-semibold">✅ {result.message || t('upload.success')}</div>
                  <div>Repo: <strong>{result.repoName}</strong> · Commit: <code>{result.commitHash}</code></div>
                  {result.imageRef ? <div>Source: <code>{result.imageRef}</code></div> : null}
                  {result.chartPath ? <div>Source: <code>{result.chartPath}</code></div> : null}
                  {result.terraformPath ? <div>Source: <code>{result.terraformPath}</code></div> : null}
                  <button
                    className="ce-btn ce-btn--sm ce-btn--primary mt-2"
                    onClick={() => navigate(`/RepoDetails/${encodeURIComponent(result.repoName)}?tab=overview&sbom=${encodeURIComponent(result.commitHash)}`)}
                  >
                    {t('upload.viewAnalysis')}
                  </button>
                </div>
              )}

              {/* Upload button */}
              {!result && (
                <button
                  className="ce-btn ce-btn--primary"
                  onClick={handleUpload}
                  disabled={uploading || !repoName.trim() || (sourceType === 'upload' ? !file : sourceType === 'docker-image' ? !imageRef.trim() : sourceType === 'helm-chart' ? !chartPath.trim() : !terraformPath.trim())}
                >
                  {uploading
                    ? <><span className="spinner-border spinner-border-sm me-2" />{sourceType === 'docker-image' ? t('upload.generatingDocker') : sourceType === 'helm-chart' ? t('upload.generatingHelm') : sourceType === 'terraform' ? t('upload.generatingTerraform') : t('upload.uploading')}</>
                    : sourceType === 'docker-image'
                      ? `🐳 ${t('upload.generateDockerBtn')}`
                      : sourceType === 'helm-chart'
                        ? `☸️ ${t('upload.generateHelmBtn')}`
                        : sourceType === 'terraform'
                          ? `🏗️ ${t('upload.generateTerraformBtn')}`
                      : `⬆️ ${t('upload.uploadBtn')}`}
                </button>
              )}

              {result && (
                <button className="ce-btn ce-btn--sm ce-btn--secondary" onClick={() => { setResult(null); setFile(null); setRepoName(''); setImageRef(''); setChartPath(''); setTerraformPath(''); }}>
                  {t('upload.uploadAnother')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: Documentation */}
        <div className="col-lg-7">
          <div className="ce-card">
            <div className="ce-card-header">
              📖 {t('upload.docsCard')}
            </div>
            <div className="ce-card-body">

              <p className="text-secondary small mb-3">
                {t('upload.docsIntro')}
              </p>

              {/* Tool tabs */}
              <ul className="nav nav-pills mb-3 gap-1">
                {TOOLS.map(tool => (
                  <li key={tool.id} className="nav-item">
                    <button
                      className={`nav-link py-1 px-3 ${activeTool === tool.id ? 'active' : 'text-secondary'}`}
                      onClick={() => setActiveTool(tool.id)}
                    >
                      {tool.label}
                      <span className={`badge ms-2 ${tool.badgeClass}`} style={{ fontSize: '0.65rem' }}>{t(`upload.tools.${tool.id}.badge`)}</span>
                    </button>
                  </li>
                ))}
              </ul>

              {activeTool_ && (
                <div>
                  <p className="small text-secondary mb-3">{t(`upload.tools.${activeTool_.id}.description`)}</p>

                  {/* Install */}
                  <div className="mb-3">
                    <div className="small fw-semibold text-secondary mb-1">📦 {t('upload.install')}</div>
                    <div className="bg-dark text-light rounded p-2 d-flex align-items-start justify-content-between" style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                      <code style={{ color: '#a8ff78', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{activeTool_.install}</code>
                      <CopyButton text={activeTool_.install} />
                    </div>
                  </div>

                  {/* Commands */}
                  <div className="small fw-semibold text-secondary mb-2">🚀 {t('upload.generate')}</div>
                  <div className="d-flex flex-column gap-2">
                    {activeTool_.commands.map((cmd, i) => (
                      <div key={i}>
                        <div className="text-secondary mb-1" style={{ fontSize: '0.75rem' }}>▸ {cmd.label}</div>
                        <div className="bg-dark text-light rounded p-2 d-flex align-items-start justify-content-between" style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                          <code style={{ color: '#79c0ff', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{cmd.code}</code>
                          <CopyButton text={cmd.code} />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Tips */}
                  <div className="mt-4 p-3 rounded border bg-light" style={{ fontSize: '0.8rem' }}>
                    <strong>💡 {t('upload.tips.title')}</strong>
                    <ul className="mb-0 mt-1 ps-3">
                      <li>{t('upload.tips.root')}</li>
                      <li>{t('upload.tips.format')}</li>
                      <li>{t('upload.tips.cdx')}</li>
                      <li>{t('upload.tips.name')}</li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Format info card */}
          <div className="ce-card mt-3">
            <div className="ce-card-body py-3">
              <div className="row g-3 text-center">
                <div className="col-6">
                  <div className="fw-semibold small">{t('upload.formats.spdxTitle')}</div>
                  <div className="text-secondary" style={{ fontSize: '0.75rem' }}>{t('upload.formats.spdxDesc')}</div>
                  <div className="text-secondary" style={{ fontSize: '0.75rem' }}>{t('upload.formats.spdxField')}</div>
                </div>
                <div className="col-6">
                  <div className="fw-semibold small">{t('upload.formats.cdxTitle')}</div>
                  <div className="text-secondary" style={{ fontSize: '0.75rem' }}>{t('upload.formats.cdxDesc')}</div>
                  <div className="text-secondary" style={{ fontSize: '0.75rem' }}>{t('upload.formats.cdxField')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export default SbomUpload;
